import type { SegmentSplit, SegmentSplitInput } from '../classification.js';
import type {
  ApRecurrencyFrequency,
  PurchaseOrderStatus,
  PurchaseOrderType,
  PurchaseRequestStatus,
  PurchaseRequestType,
} from '../generated/enums.js';
import type { ProcurementThreeWayReceiptGate } from '../procurement-settings.js';
import type { ResolvedDimensionName } from './classification.js';

export interface ProcurementDimensionInput {
  costCenterId?: string | null;
  departmentId?: string | null;
  managerialAccountId?: string | null;
  glAccountId?: string | null;
  segments?: SegmentSplitInput[] | null;
}

export interface ProcurementLineItemInput extends ProcurementDimensionInput {
  lineNumber: number;
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  lineAmount: number;
}

export interface CreatePurchaseRequestRequest extends ProcurementDimensionInput {
  entityId: string;
  vendorIds?: string[];
  title: string;
  description?: string | null;
  needByDate: string;
  purchaseType: PurchaseRequestType;
  estimatedAmount: number;
  budgetRequestedAmount: number;
  finalAmount?: number | null;
  paymentTerms?: string | null;
  responsibleUserId?: string | null;
  currencyCode?: string;
  lineItems: ProcurementLineItemInput[];
}

export interface UpdatePurchaseRequestRequest extends Partial<
  Omit<CreatePurchaseRequestRequest, 'number'>
> {
  lineItems?: ProcurementLineItemInput[];
}

export interface UpdatePurchaseOrderRequest extends Partial<
  Omit<CreatePurchaseOrderRequest, 'entityId' | 'number' | 'purchaseRequestId'>
> {
  paymentTerms?: string | null;
  responsibleUserId?: string | null;
  budgetRequestedAmount?: number | null;
  finalAmount?: number | null;
  poType?: PurchaseOrderType;
  expectedDeliveryDate?: string | null;
  overBillingTolerancePercent?: number | null;
  overBillingToleranceAmount?: number | null;
  threeWayReceiptGate?: ProcurementThreeWayReceiptGate | null;
  lineItems?: ProcurementLineItemInput[];
}

export interface PurchaseOrderLinkedBillSummary {
  billId: string;
  billNumber: string | null;
  status: string;
  totalAmount: number;
  dueDate: string | null;
}

export interface ConvertPurchaseRequestToPurchaseOrderRequest extends RecurringPurchaseOrderInput {
  number?: string;
  poType?: PurchaseOrderType;
  referenceDate?: string;
  vendorIds?: string[];
}

export interface ApprovePurchaseRequestStepRequest {
  comment?: string;
  confirmNoBudgetCommitment?: boolean;
}

export const PURCHASE_REQUEST_BUDGET_CONFIRMATION_CODE =
  'BUDGET_COMMITMENT_CONFIRMATION_REQUIRED' as const;

export interface PurchaseRequestBudgetConfirmationRequiredBody {
  code: typeof PURCHASE_REQUEST_BUDGET_CONFIRMATION_CODE;
  message: string;
  unmatchedLineNumbers: number[];
}

export const PURCHASE_REQUEST_BUDGET_EXCEEDED_CODE = 'BUDGET_EXCEEDED' as const;
export const PURCHASE_REQUEST_BUDGET_EXCEPTION_REQUIRED_CODE = 'BUDGET_EXCEPTION_REQUIRED' as const;

export interface PurchaseRequestBudgetBlockBody {
  code:
    | typeof PURCHASE_REQUEST_BUDGET_EXCEEDED_CODE
    | typeof PURCHASE_REQUEST_BUDGET_EXCEPTION_REQUIRED_CODE;
  message: string;
  budgetCheckResult: import('../budget.js').BudgetCheckResult;
}

export const PURCHASE_ORDER_BUDGET_EXCEPTION_REQUIRED_CODE =
  'PURCHASE_ORDER_BUDGET_EXCEPTION_REQUIRED' as const;

/**
 * A HARD_BLOCK budget result means no exception can be requested at all — the PR path
 * already distinguishes this (`PURCHASE_REQUEST_BUDGET_EXCEEDED_CODE`) from the
 * REQUIRE_EXCEPTION case. The PO path previously reused
 * `PURCHASE_ORDER_BUDGET_EXCEPTION_REQUIRED_CODE` for both, which told the web client an
 * exception button belonged on a BLOCK it could never resolve (ALC-2946 M15).
 */
export const PURCHASE_ORDER_BUDGET_HARD_BLOCK_CODE = 'PURCHASE_ORDER_BUDGET_HARD_BLOCK' as const;

export interface MarkPurchaseOrderReadyRequest {
  vendorIds: string[];
  paymentTerms: string;
  finalAmount: number;
  responsibleUserId: string;
  confirmBudgetException?: boolean;
  draftBillVendorId?: string;
  expectedDeliveryDate?: string | null;
  threeWayReceiptGate?: ProcurementThreeWayReceiptGate | null;
}

export interface RecordPurchaseRequestVendorQuoteRequest {
  vendorId: string;
  quotedAmount: number;
  quoteValidUntil?: string | null;
  note?: string | null;
}

export interface SelectPurchaseRequestVendorRequest {
  vendorId: string;
}

export interface RecordPurchaseOrderVendorQuoteRequest {
  vendorId: string;
  quotedAmount: number;
  quoteValidUntil?: string | null;
  note?: string | null;
}

export interface SelectPurchaseOrderVendorRequest {
  vendorId: string;
}

export interface ProcurementQuoteAttachmentResponse {
  documentId: string;
  originalName: string;
  mimeType: string;
  fileSizeBytes: number;
  createdAt: string;
}

export interface ProcurementVendorQuoteResponse {
  vendorId: string;
  quotedAmount: number | null;
  quoteValidUntil: string | null;
  selected: boolean;
  note: string | null;
  attachments: ProcurementQuoteAttachmentResponse[];
}

export interface PurchaseRequestQuoteRequirementResponse {
  enabled: boolean;
  minimum: number | null;
  registered: number;
  hasSelectedWinner: boolean;
  readyForConversion: boolean;
  blockers: string[];
}

export interface ReceivePurchaseOrderRequest {
  receivedByUserId: string;
  note?: string | null;
  lines: {
    lineItemId: string;
    receivedQuantity: number;
  }[];
}

export interface LinkBillToPurchaseOrderRequest {
  billId: string;
}

/**
 * Remaps a purchase order's GL classification only (ALC-2003) — the authorized remediation path
 * for a classification that went stale after a CoA chart cutover. Never touches amounts,
 * vendors, dates, or status; available for any order that has not reached a terminal status
 * (CANCELLED/SETTLED/COMPLETED), unlike the OPEN-only general update.
 */
export interface RemapPurchaseOrderGlClassificationRequest {
  glAccountId?: string | null;
  lineItems?: { lineItemId: string; glAccountId: string | null }[];
  /**
   * Resolves the target CoA version as of this date instead of the entity's currently active
   * version — pass the bill's own competency/issue date so the remap targets exactly the
   * version that bill resolves to, even across more than one chart cutover.
   */
  accountingDate?: string | null;
}

export interface RecurringPurchaseOrderInput {
  recurrenceFrequency?: ApRecurrencyFrequency | null;
  recurringSince?: string | null;
  recurringUntil?: string | null;
  perOccurrenceAmount?: number | null;
}

export interface CreatePurchaseOrderRequest
  extends ProcurementDimensionInput, RecurringPurchaseOrderInput {
  entityId: string;
  vendorIds?: string[];
  number?: string;
  title: string;
  description?: string | null;
  purchaseRequestId?: string | null;
  totalAmount: number;
  currencyCode?: string;
  poType?: PurchaseOrderType;
  lineItems: ProcurementLineItemInput[];
}

export type ProcurementSortDirection = 'asc' | 'desc';
export type PurchaseRequestSortBy =
  | 'number'
  | 'title'
  | 'requester'
  | 'status'
  | 'estimatedAmount'
  | 'createdAt'
  | 'needByDate'
  | 'relatedOrders';
export type PurchaseOrderSortBy =
  | 'number'
  | 'title'
  | 'status'
  | 'totalAmount'
  | 'approvedAt'
  | 'readyAt'
  | 'expiresAt'
  | 'originRequest';

export interface ListPurchaseRequestsQuery extends ProcurementDimensionInput {
  entityId?: string;
  vendorId?: string;
  status?: PurchaseRequestStatus;
  requestedByUserId?: string;
  sortBy?: PurchaseRequestSortBy;
  sortDirection?: ProcurementSortDirection;
}

export interface ListPurchaseOrdersQuery extends ProcurementDimensionInput {
  entityId?: string;
  vendorId?: string;
  status?: PurchaseOrderStatus;
  purchaseRequestId?: string;
  activeOnly?: boolean;
  sortBy?: PurchaseOrderSortBy;
  sortDirection?: ProcurementSortDirection;
}

export type PurchaseOrderValidationIssueCode =
  | 'EXPIRED'
  | 'FULFILLED'
  | 'NOT_READY'
  | 'NOT_RECEIVED'
  | 'AMOUNT_EXCEEDED'
  | 'VENDOR_NOT_ALLOWED'
  | 'GL_CLASSIFICATION_STALE'
  | 'GL_CLASSIFICATION_INCOMPLETE';

export interface PurchaseOrderValidationIssue {
  code: PurchaseOrderValidationIssueCode;
  message: string;
}

export interface PurchaseOrderValidationResult {
  purchaseOrderId: string;
  isValid: boolean;
  availableAmount: number;
  issues: PurchaseOrderValidationIssue[];
}

export interface PurchaseRequestLineResponse extends ProcurementLineItemInput {
  tenantId: string;
  purchaseRequestId: string;
  lineItemId: string;
  /** CoA version `glAccountId` was resolved against when last written (ALC-2003). */
  coaVersionId: string | null;
  /**
   * True when `coaVersionId` no longer matches the entity's currently active CoA version —
   * a chart cutover happened after this line was classified.
   */
  glAccountStale: boolean;
  createdAt: string;
}

export interface PurchaseOrderLineResponse extends ProcurementLineItemInput {
  tenantId: string;
  purchaseOrderId: string;
  lineItemId: string;
  receivedQuantity?: number | null;
  /** CoA version `glAccountId` was resolved against when last written (ALC-2003). */
  coaVersionId: string | null;
  /** See `PurchaseRequestLineResponse.glAccountStale`. */
  glAccountStale: boolean;
  createdAt: string;
}

export interface PurchaseRequestResponse {
  tenantId: string;
  purchaseRequestId: string;
  entityId: string;
  number: string;
  vendorIds: string[];
  title: string;
  description: string | null;
  costCenterId: string | null;
  departmentId: string | null;
  managerialAccountId: string | null;
  glAccountId: string | null;
  /** CoA version `glAccountId` was resolved against when last written (ALC-2003). */
  coaVersionId: string | null;
  /** See `PurchaseRequestLineResponse.glAccountStale`. */
  glAccountStale: boolean;
  /** Resolved dimension names, mirroring the bills pending-approval read's shape. Null when unclassified. */
  costCenter?: ResolvedDimensionName | null;
  department?: ResolvedDimensionName | null;
  managerialAccount?: ResolvedDimensionName | null;
  glAccount?: ResolvedDimensionName | null;
  segments?: SegmentSplit[] | null;
  estimatedAmount: number;
  budgetRequestedAmount: number;
  finalAmount: number | null;
  paymentTerms: string | null;
  currencyCode: string;
  needByDate: string | null;
  purchaseType: PurchaseRequestType;
  status: PurchaseRequestStatus;
  responsibleUserId: string | null;
  responsibleUser?: { name: string } | null;
  submittedForApprovalAt: string | null;
  submittedByUserId: string | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  rejectedAt: string | null;
  rejectedByUserId: string | null;
  rejectionReason: string | null;
  requestedByUserId: string | null;
  requestedByUser?: { name: string } | null;
  createdAt: string;
  updatedAt: string | null;
  lineItems: PurchaseRequestLineResponse[];
  vendorQuotes: ProcurementVendorQuoteResponse[];
  quoteRequirement: PurchaseRequestQuoteRequirementResponse | null;
}

export interface PurchaseOrderResponse {
  tenantId: string;
  purchaseOrderId: string;
  entityId: string;
  number: string;
  purchaseRequestId: string | null;
  vendorIds: string[];
  title: string;
  description: string | null;
  costCenterId: string | null;
  departmentId: string | null;
  managerialAccountId: string | null;
  glAccountId: string | null;
  /** CoA version `glAccountId` was resolved against when last written (ALC-2003). */
  coaVersionId: string | null;
  /** See `PurchaseRequestLineResponse.glAccountStale`. */
  glAccountStale: boolean;
  segments?: SegmentSplit[] | null;
  totalAmount: number;
  budgetRequestedAmount: number | null;
  finalAmount: number | null;
  billedAmount: number;
  paymentTerms: string | null;
  currencyCode: string;
  poType: PurchaseOrderType;
  recurrenceFrequency?: ApRecurrencyFrequency | null;
  recurringSince?: string | null;
  recurringUntil?: string | null;
  perOccurrenceAmount?: number | null;
  vendorRecurringBillId?: string | null;
  status: PurchaseOrderStatus;
  responsibleUserId: string | null;
  responsibleUser?: { name: string } | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  readyAt: string | null;
  readyByUserId: string | null;
  fulfilledAt: string | null;
  fulfilledByUserId: string | null;
  receivedAt: string | null;
  receivedByUserId: string | null;
  expectedDeliveryDate: string | null;
  overBillingTolerancePercent: number | null;
  overBillingToleranceAmount: number | null;
  threeWayReceiptGate: ProcurementThreeWayReceiptGate | null;
  settledAt: string | null;
  expiresAt: string | null;
  cancelledAt: string | null;
  cancelledByUserId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string | null;
  lineItems: PurchaseOrderLineResponse[];
  vendorQuotes: ProcurementVendorQuoteResponse[];
}

/** One recorded receipt, scoped to its purchase order for direct investigation links. */
export interface PurchaseOrderReceiptResponse {
  receiptId: string;
  purchaseOrderId: string;
  receivedAt: string;
  note: string | null;
  lines: {
    receiptLineId: string;
    lineItemId: string;
    lineNumber: number;
    description: string;
    receivedQuantity: string;
  }[];
}
